import React from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useRacha } from '@/contexts/RachaContext';
import { Cloud, Database, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react-native';

interface MigrationModalProps {
  visible: boolean;
  onClose: () => void;
  onMigrate: () => void;
  onSkip: () => void;
}

export default function MigrationModal({ visible, onClose, onMigrate, onSkip }: MigrationModalProps) {
  const { colors } = useTheme();
  const { migrationProgress, loading } = useRacha();

  const styles = StyleSheet.create({
    overlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 24,
    },
    container: {
      backgroundColor: colors.surface,
      borderRadius: 20,
      padding: 24,
      width: '100%',
      maxWidth: 400,
      maxHeight: '80%',
    },
    header: {
      alignItems: 'center',
      marginBottom: 24,
    },
    iconContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 16,
    },
    iconWrapper: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: colors.primary + '20',
      justifyContent: 'center',
      alignItems: 'center',
    },
    arrowContainer: {
      marginHorizontal: 12,
    },
    title: {
      fontSize: 20,
      fontFamily: 'Inter-Bold',
      color: colors.text,
      textAlign: 'center',
      marginBottom: 8,
    },
    subtitle: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 20,
    },
    content: {
      marginBottom: 24,
    },
    benefitsList: {
      marginTop: 16,
    },
    benefitItem: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
    },
    benefitIcon: {
      marginRight: 12,
    },
    benefitText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      flex: 1,
    },
    progressContainer: {
      marginBottom: 24,
    },
    progressHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    progressText: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
    },
    progressPercentage: {
      fontSize: 14,
      fontFamily: 'Inter-Bold',
      color: colors.primary,
    },
    progressBar: {
      height: 8,
      backgroundColor: colors.border,
      borderRadius: 4,
      overflow: 'hidden',
      marginBottom: 8,
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.primary,
      borderRadius: 4,
    },
    currentItem: {
      fontSize: 12,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    errorContainer: {
      backgroundColor: colors.error + '20',
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
    },
    errorText: {
      fontSize: 14,
      fontFamily: 'Inter-Regular',
      color: colors.error,
      textAlign: 'center',
    },
    successContainer: {
      backgroundColor: colors.success + '20',
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      alignItems: 'center',
    },
    successText: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.success,
      textAlign: 'center',
      marginTop: 8,
    },
    buttons: {
      flexDirection: 'row',
      gap: 12,
    },
    button: {
      flex: 1,
      paddingVertical: 14,
      paddingHorizontal: 20,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
    },
    primaryButton: {
      backgroundColor: colors.primary,
    },
    secondaryButton: {
      backgroundColor: colors.border,
    },
    buttonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
    },
    primaryButtonText: {
      color: '#FFFFFF',
    },
    secondaryButtonText: {
      color: colors.text,
    },
    loadingContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    loadingText: {
      marginLeft: 8,
      fontSize: 16,
      fontFamily: 'Inter-Medium',
      color: '#FFFFFF',
    },
  });

  const renderContent = () => {
    if (migrationProgress) {
      const progress = migrationProgress.total > 0 ? migrationProgress.completed / migrationProgress.total : 0;
      const percentage = Math.round(progress * 100);

      if (migrationProgress.errors.length > 0) {
        return (
          <>
            <View style={styles.errorContainer}>
              <AlertCircle size={24} color={colors.error} style={{ alignSelf: 'center', marginBottom: 8 }} />
              <Text style={styles.errorText}>
                Ocorreram alguns erros durante a migração:
              </Text>
              {migrationProgress.errors.slice(0, 3).map((error, index) => (
                <Text key={index} style={[styles.errorText, { fontSize: 12, marginTop: 4 }]}>
                  • {error}
                </Text>
              ))}
            </View>
          </>
        );
      }

      if (percentage === 100) {
        return (
          <View style={styles.successContainer}>
            <CheckCircle size={32} color={colors.success} />
            <Text style={styles.successText}>
              Migração concluída com sucesso! 🎉
            </Text>
            <Text style={[styles.successText, { fontSize: 12, marginTop: 4 }]}>
              Seus dados agora estão seguros na nuvem
            </Text>
          </View>
        );
      }

      return (
        <View style={styles.progressContainer}>
          <View style={styles.progressHeader}>
            <Text style={styles.progressText}>Migrando seus dados...</Text>
            <Text style={styles.progressPercentage}>{percentage}%</Text>
          </View>
          <View style={styles.progressBar}>
            <View style={[styles.progressFill, { width: `${percentage}%` }]} />
          </View>
          <Text style={styles.currentItem}>
            {migrationProgress.current || 'Preparando...'}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.content}>
        <Text style={styles.subtitle}>
          Encontramos dados salvos localmente. Que tal migrar para a nuvem e ter acesso em qualquer dispositivo?
        </Text>
        
        <View style={styles.benefitsList}>
          <View style={styles.benefitItem}>
            <CheckCircle size={20} color={colors.success} style={styles.benefitIcon} />
            <Text style={styles.benefitText}>Acesso em qualquer dispositivo</Text>
          </View>
          <View style={styles.benefitItem}>
            <CheckCircle size={20} color={colors.success} style={styles.benefitIcon} />
            <Text style={styles.benefitText}>Backup automático na nuvem</Text>
          </View>
          <View style={styles.benefitItem}>
            <CheckCircle size={20} color={colors.success} style={styles.benefitIcon} />
            <Text style={styles.benefitText}>Sincronização em tempo real</Text>
          </View>
          <View style={styles.benefitItem}>
            <CheckCircle size={20} color={colors.success} style={styles.benefitIcon} />
            <Text style={styles.benefitText}>Compartilhamento real entre usuários</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderButtons = () => {
    if (migrationProgress) {
      if (migrationProgress.completed === migrationProgress.total && migrationProgress.errors.length === 0) {
        return (
          <View style={styles.buttons}>
            <TouchableOpacity
              style={[styles.button, styles.primaryButton]}
              onPress={onClose}
              activeOpacity={0.8}
            >
              <Text style={[styles.buttonText, styles.primaryButtonText]}>Continuar</Text>
            </TouchableOpacity>
          </View>
        );
      }
      return null; // Não mostrar botões durante migração
    }

    return (
      <View style={styles.buttons}>
        <TouchableOpacity
          style={[styles.button, styles.secondaryButton]}
          onPress={onSkip}
          activeOpacity={0.8}
        >
          <Text style={[styles.buttonText, styles.secondaryButtonText]}>Talvez Depois</Text>
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[styles.button, styles.primaryButton]}
          onPress={onMigrate}
          disabled={loading}
          activeOpacity={0.8}
        >
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="small" color="#FFFFFF" />
              <Text style={styles.loadingText}>Migrando...</Text>
            </View>
          ) : (
            <Text style={[styles.buttonText, styles.primaryButtonText]}>Migrar Agora</Text>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.container}>
          <View style={styles.header}>
            <View style={styles.iconContainer}>
              <View style={styles.iconWrapper}>
                <Database size={24} color={colors.primary} />
              </View>
              <View style={styles.arrowContainer}>
                <ArrowRight size={20} color={colors.textSecondary} />
              </View>
              <View style={styles.iconWrapper}>
                <Cloud size={24} color={colors.primary} />
              </View>
            </View>
            <Text style={styles.title}>Migrar para a Nuvem</Text>
          </View>

          {renderContent()}
          {renderButtons()}
        </View>
      </View>
    </Modal>
  );
}
